/* eslint-disable @next/next/no-img-element */
'use client'
// import { Image } from '@/components'
import { URL_ICON, URL_IMG } from '@/constants/ui'
import { CommonTypeSelectedProduct } from '@/contexts'
import { getVND } from '@/utils/helpers'
import GradeIcon from '@mui/icons-material/Grade'
import {
  alpha,
  Box,
  Button,
  Card,
  CardProps,
  CircularProgress,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material'
import { TProductSelected, useAddToCart } from '@/features/cart/hooks/use-add-to-cart'
import { scrollBodyHide, scrollBodyShow } from '@/utils/copy-content'
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart'
import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import { AnimatePresence, motion } from 'framer-motion'
import Image from 'next/image'
import { customImageLoader } from '@/utils/image_loader'
import { useRouter } from 'next/navigation'
import { useMemo, useState } from 'react'
import { withProductDetailProvider } from '../../contexts/hoc-context'
import { useProductDetailV2State } from '../../contexts/product-detail'
import { ListOptions } from '../../pages/product-detail/components/stickybar-bottom'
import { getImagesByColor, isStartWithHttp, PREFIX_URL } from '../../utils/common'
import ColorOptionItem from '../product-detail/select-color'
import { SelectQuantity } from '../product-detail/select-quantity'
import SelectSize from '../product-detail/select-size'
import { CardSkeleton } from '@/components'
type Props = {
  optionsColor?: {
    position: 'left' | 'right'
  }
  cardProps?: Omit<CardProps, 'children'>
  product: Product
  showSetItems?: boolean
  showColors?: boolean // Có hiển thị tùy chọn màu sắc hay không
  defaultColorUid?: string // UID của màu mặc định muốn hiển thị
  salesInfo?: IDailySaleItem
  ongoing?: boolean
}

const ProductItemCore = ({
  optionsColor,
  cardProps,
  showSetItems = true,
  product,
  showColors,
  salesInfo,
  ongoing = true,
}: Props) => {
  const {
    sortedOptions,
    productSelected,
    getAvailableOptions,
    selectedOptions,
    handleOptionChange,
    getCurrentShirtBack,
  } = useProductDetailV2State()

  const [isHovering, setIsHovering] = useState(false)
  const option = sortedOptions?.find((option) => option?.attribute_code === 'color')
  const availableOptions = option ? getAvailableOptions(option?.attribute_code) : []
  const theme = useTheme()
  const mainImage = productSelected?.product?.image?.url || product?.image?.url || ''
  const router = useRouter()
  const saleQty = salesInfo?.sale_qty || 0
  const imageHover = getCurrentShirtBack()

  const typeProduct = product?.__typename
  // Use regular state instead of useOptimistic
  const link = '/products/' + (product?.url_key || '/')

  const buildLinkWithParams = () => {
    const params = new URLSearchParams()

    // Nếu có salesInfo với configurable_options, ưu tiên sử dụng options này
    if (salesInfo?.configurable_options && salesInfo.configurable_options.length > 0) {
      salesInfo.configurable_options.forEach((configOption) => {
        if (configOption?.attribute_code && configOption?.values?.[0]?.uid) {
          params.set(configOption.attribute_code, configOption.values[0].uid)
        }
      })
    }
    // Nếu không có salesInfo, sử dụng selectedOptions như bình thường
    else if (option && selectedOptions?.[option.attribute_code]) {
      const selectedUid = selectedOptions[option.attribute_code]
      if (selectedUid) {
        params.set(option.attribute_code, selectedUid)
      }
    }

    return params.toString() ? `${link}?${params.toString()}` : link
  }
  const price = useMemo(() => {
    const priceSale = salesInfo?.sale_price || 0
    const finalPrice =
      priceSale ||
      product?.price_range?.minimum_price?.final_price?.value ||
      productSelected?.product?.price_range?.minimum_price?.final_price?.value ||
      0
    const regularPrice =
      product?.price_range?.minimum_price?.regular_price?.value ||
      productSelected?.product?.price_range?.minimum_price?.regular_price?.value ||
      0

    const percentSale = Math.ceil(((regularPrice - finalPrice) / regularPrice) * 100)
    return {
      final: finalPrice,
      regular: regularPrice,
      percentSale: percentSale,
    }
  }, [
    product?.price_range?.minimum_price,
    productSelected?.product?.price_range?.minimum_price,
    salesInfo?.sale_price,
  ])

  const isFlashSale = product?.daily_sale
  const handleCardClick = () => {
    const linkWithParams = buildLinkWithParams()
    router.push(linkWithParams)
  }
  const MotionCard = motion.div
  // Thêm cái này để không bị giựt UI hình MAIN
  if (!productSelected) {
    return <CardSkeleton />
  }
  return (
    <MotionCard initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <Card
        {...cardProps}
        sx={{
          minWidth: { xs: 280, sm: 300, md: 320 },
          borderRadius: 2,
          overflow: 'visible',
          boxShadow: 'none',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          ...cardProps?.sx,
        }}
      >
        {/* Main product image container */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '1',
            borderRadius: 2,
            overflow: 'hidden',
            mb: 2,
          }}
        >
          {price.percentSale > 0 && ongoing && !isFlashSale && (
            <Typography
              component={'span'}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                bgcolor: '#C84B00',
                color: '#fff',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                zIndex: 5,
                fontSize: '0.825rem',
                fontWeight: 600,
              }}
            >
              {price.percentSale}%
            </Typography>
          )}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              ...(imageHover && {
                '@media (hover: hover)': {
                  '&:hover .image-hover': {
                    opacity: 1,
                  },
                  '&:hover .image-default': {
                    opacity: 0,
                  },
                },
              }),
            }}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <ChangeImageOnHover
              imageDefault={mainImage}
              imageHover={imageHover}
              product={product}
              handleCardClick={handleCardClick}
            />
            <BoxSize
              isHovering={isHovering}
              isShowAddToCart={typeProduct === 'SimpleProduct'}
              dailySale={salesInfo}
            />
            <IconCartMobile
              product={product}
              isClickAddCart={typeProduct === 'SimpleProduct'}
              dailySale={salesInfo}
            />
          </Box>
        </Box>
        <Stack flex={1} onClick={handleCardClick}>
          {/* Product details */}
          {showColors && (
            <Box sx={{ px: 0, cursor: 'grab' }}>
              {option && (
                <>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      mb: 1,
                      overflowX: 'auto',
                      overflowY: 'hidden',
                      // Ẩn scrollbar trên mọi trình duyệt nhưng vẫn cho phép scroll ngang
                      scrollbarWidth: 'none', // Firefox
                      msOverflowStyle: 'none', // IE/Edge legacy
                      '&::-webkit-scrollbar': {
                        display: 'none', // Chrome/Safari
                      },
                    }}
                  >
                    {availableOptions?.map((value, index, arg) => {
                      const isSelected = selectedOptions?.[option?.attribute_code] === value?.uid
                      const isDisabled = !value?.hasStock

                      if (option.attribute_code === 'color') {
                        return (
                          <ColorOptionItem
                            key={value.uid}
                            value={value}
                            isSelected={isSelected}
                            isDisabled={isDisabled}
                            trigger="hover"
                            onAction={(uid) => handleOptionChange(option.attribute_code, uid)}
                          />
                        )
                      }

                      // Render text option
                      return null
                    })}
                  </Stack>
                </>
              )}
            </Box>
          )}
          {/* Product details */}
          <Stack sx={{ px: 0, gap: 1 }} flex={1}>
            {/* Product name and price */}
            <Stack direction="column" gap={1} height={'100%'}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '0.875rem', md: '1.125rem' },
                  fontWeight: 400,
                  lineHeight: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  // mb: 0.5,
                }}
              >
                {product?.name}{' '}
                {!showColors && productSelected?.product?.__typename === 'SimpleProduct'
                  ? ` - ${productSelected?.attributes?.find((attr) => attr?.code === 'color')?.label}`
                  : ''}
              </Typography>
              {price?.final > 0 && ongoing ? (
                <Typography
                  sx={{
                    fontSize: { xs: '1rem', md: '1.225rem' },
                    fontWeight: 600,
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    fontFamily: 'Oswald',
                    color: 'five.main',
                  }}
                >
                  {getVND(price.final)}
                  {price.final < price.regular && (
                    <Typography
                      component={'span'}
                      sx={{
                        ml: 1,
                        fontSize: '0.875rem',
                        fontWeight: 400,
                        lineHeight: 1.5,
                        textDecoration: 'line-through',
                        color: '#707070',
                      }}
                    >
                      {getVND(price.regular)}
                      {/* <Typography component="span" color="inherit">
                        /sản phẩm
                      </Typography> */}
                    </Typography>
                  )}
                </Typography>
              ) : (
                <Typography
                  sx={{
                    fontSize: { xs: '0.875rem', md: '1.225rem' },
                    fontWeight: 600,
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    fontFamily: 'Oswald',
                  }}
                >
                  {getVND(price.final)}
                  {price.final < price.regular && (
                    <Typography
                      component={'span'}
                      sx={{
                        ml: 1,
                        fontSize: '0.875rem',
                        fontWeight: 400,
                        lineHeight: 1.5,
                        textDecoration: 'line-through',
                        color: '#707070',
                      }}
                    >
                      {getVND(price.regular)}
                    </Typography>
                  )}
                </Typography>
              )}
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                    border: '1px solid',
                    borderColor: 'active.main',
                    borderRadius: '4px',
                    bgcolor: alpha(theme.palette.active.main, 0.1),
                    px: 0.5,
                  }}
                >
                  <GradeIcon style={{ color: theme.palette.active.main }} fontSize="small" />{' '}
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      lineHeight: 1.5,
                    }}
                  >
                    ({product?.rating_summary || 5})
                  </Typography>
                </Box>
                {product?.sales_qty && (
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 400,
                      lineHeight: 1.5,
                      color: 'primary.main',
                    }}
                  >
                    {`Đã bán ${product?.sales_qty}`}
                  </Typography>
                )}
              </Stack>
              {/* Thanh progress sale */}
              {salesInfo && (
                <Stack
                  justifyContent="center"
                  sx={{
                    mt: 'auto !important',
                    height: '24px',
                    width: '100%',
                    backgroundColor: '#FEF0E6',
                    borderRadius: '999px',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <Box
                    typography={'span'}
                    position={'absolute'}
                    width={`${Math.min(((salesInfo.sold_qty || 0) / saleQty) * 100, 100)}%`}
                    left={0}
                    height={'100%'}
                    bgcolor={'#F46A00'}
                    borderRadius={'inherit'}
                  ></Box>
                  {salesInfo.sold_qty > 0 && (
                    <Image
                      src={URL_ICON + 'ic-hot.svg'}
                      alt="ic-hot"
                      fill
                      loader={customImageLoader}
                      style={{
                        position: 'absolute',
                        left: '0',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        aspectRatio: '1/1',
                      }}
                    />
                  )}
                  <Typography textAlign={'center'} fontSize={'14px'} position={'relative'} zIndex={1}>
                    Đã bán <Box component="span">{salesInfo.sold_qty}</Box>
                    <Box component="span">/{saleQty}</Box>{' '}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Card>
    </MotionCard>
  )
}

const BoxSize = ({
  isHovering,
  isShowAddToCart,
  dailySale,
}: {
  isHovering: boolean
  isShowAddToCart: boolean
  dailySale: IDailySaleItem | undefined
}) => {
  const {
    sortedOptions,
    getAvailableOptions,
    productAddToCart,
    handleOptionChange,
    productSelected,
    selectedOptions,
  } = useProductDetailV2State()
  const [loadingSizeUid, setLoadingSizeUid] = useState<string | null>(null)
  const [loadingButton, setLoadingButton] = useState(false)
  const sizeOption = sortedOptions?.find((option) => option?.attribute_code === 'size')
  const availableSizes = sizeOption ? getAvailableOptions(sizeOption?.attribute_code) : []
  const { handleAddToCart } = useAddToCart()

  const handleAddCart = async (sizeUid: string) => {
    if (!sizeOption) return

    setLoadingSizeUid(sizeUid)

    handleOptionChange(sizeOption.attribute_code, sizeUid, async (productToAdd) => {
      try {
        await handleAddToCart({
          productSelected: productToAdd as TProductSelected[],
          quantity: 1,
        })
      } catch (error) {
        console.log('Error in Add to Cart:', error)
      } finally {
        setLoadingSizeUid(null)
      }
    })
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 8,
        left: 8,
        right: 8,
        color: '#fff',
        borderRadius: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.65)',
        backdropFilter: 'blur(35px)',
        padding: isShowAddToCart ? '0px' : '8px 20px',
        zIndex: 2,
        display: { xs: 'none', xl: 'flex' },
        flexDirection: 'column',
        gap: 1,
        visibility: isHovering ? 'visible' : 'hidden',
        opacity: isHovering ? 1 : 0,
        transform: isHovering ? 'translateY(0)' : 'translateY(12px)',
        transition: 'all 0.3s ease-in-out',
      }}
    >
      <Box>
        {isShowAddToCart ? (
          <Button
            fullWidth
            variant="text"
            onClick={async (e) => {
              e.stopPropagation()
              setLoadingButton(true)
              // nếu có dailySale thì thêm sản phẩm cha vào giỏ với các options đã chọn
              if (dailySale) {
                await handleAddToCart({
                  productSelected: productAddToCart.map((item) => ({
                    options: dailySale?.configurable_options.reduce(
                      (acc, attribute) => {
                        if (attribute?.attribute_code && attribute?.values?.[0]?.uid) {
                          acc[attribute.attribute_code] = attribute.values[0].uid
                        }
                        return acc
                      },
                      {} as Record<string, string>
                    ),
                    parent: {
                      ...item?.product,
                      sku: dailySale?.sku_parent,
                    },
                    product: item?.product,
                  })) as TProductSelected[],
                  quantity: 1,
                })
              } else {
                // không có dailySale thì thêm sản phẩm simple đã chọn vào giỏ
                await handleAddToCart({
                  productSelected: productAddToCart,
                  quantity: 1,
                })
              }
              setLoadingButton(false)
            }}
            sx={{
              fontSize: '0.925rem',
              textTransform: 'none',
              fontWeight: 500,
              backgroundColor: 'transparent',
              padding: '8px 20px',
              transition: 'all 0.3s ease-in-out',
              '&:hover': { color: '#FF6B00' },
            }}
          >
            {loadingButton ? (
              <CircularProgress size={20} sx={{ color: 'primary.main' }} />
            ) : (
              'Thêm vào giỏ hàng +'
            )}
          </Button>
        ) : (
          <Typography textAlign={'center'} fontSize={'0.925rem'} color="primary.main">
            Thêm nhanh vào giỏ hàng +
          </Typography>
        )}
      </Box>

      {sizeOption && availableSizes.length > 0 && (
        <Stack direction={'row'} gap={1} justifyContent={'center'} flexWrap={'wrap'}>
          {availableSizes.map((value) => {
            const isDisabled = !value?.hasStock
            const isLoadingThisSize = loadingSizeUid === value.uid
            return (
              <Box key={value.uid} sx={{ position: 'relative' }}>
                <SelectSize
                  label={value.label}
                  isSelected={false}
                  isDisabled={isDisabled || !!loadingSizeUid}
                  onClick={() => {
                    handleAddCart(value.uid)
                  }}
                />
                {isLoadingThisSize && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                )}
              </Box>
            )
          })}
        </Stack>
      )}
    </Box>
  )
}

const IconCartMobile = ({
  product,
  isClickAddCart,
  dailySale,
}: {
  product: Product
  isClickAddCart: boolean
  dailySale: IDailySaleItem | undefined
}) => {
  const { productAddToCart } = useProductDetailV2State()
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const { handleAddToCart } = useAddToCart()
  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation()
    scrollBodyHide()
    setIsOpen(true)
  }

  const handleClose = () => {
    scrollBodyShow()
    setIsOpen(false)
  }
  const handleClickAddCart = async (e: React.MouseEvent) => {
    e.stopPropagation()
    setLoading(true)
    if (dailySale) {
      await handleAddToCart({
        productSelected: productAddToCart.map((item) => ({
          options: dailySale?.configurable_options.reduce(
            (acc, attribute) => {
              if (attribute?.attribute_code && attribute?.values?.[0]?.uid) {
                acc[attribute.attribute_code] = attribute.values[0].uid
              }
              return acc
            },
            {} as Record<string, string>
          ),
          parent: {
            ...item?.product,
            sku: dailySale?.sku_parent,
          },
          product: item?.product,
        })) as TProductSelected[],
        quantity: 1,
      })
    } else {
      // không có dailySale thì thêm sản phẩm simple đã chọn vào giỏ
      await handleAddToCart({
        productSelected: productAddToCart,
        quantity: 1,
      })
    }
    setLoading(false)
  }

  return (
    <>
      <Box
        onClick={!isClickAddCart ? handleOpen : handleClickAddCart}
        sx={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          display: { xs: 'flex', xl: 'none' },
          zIndex: 5,
          width: '36px',
          height: '36px',
          bgcolor: 'background.paper',
          borderRadius: '50%',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <AddShoppingCartIcon sx={{ color: 'primary.main', fontSize: 'larger', opacity: loading ? 0 : 1 }} />
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={16} />
          </Box>
        )}
      </Box>
      <QuickAddToCartSheet isOpen={isOpen} onClose={handleClose} product={product} />
    </>
  )
}

const QuickAddToCartSheet = ({
  isOpen,
  onClose,
  product,
}: {
  isOpen: boolean
  onClose: () => void
  product: Product
}) => {
  const { productAddToCart, quantity, productSelected, setQuantity } = useProductDetailV2State()
  const { handleAddToCart } = useAddToCart()
  const [isLoading, setIsLoading] = useState(false)
  const mainImage = productSelected?.product?.image?.url || product?.image?.url || ''
  const handleAddToCartAndClose = async () => {
    if (isLoading) return
    setIsLoading(true)
    try {
      await handleAddToCart({
        productSelected: productAddToCart,
        quantity: quantity,
      })
      setQuantity(1)
      setTimeout(() => {
        onClose()
      }, 300)
    } catch (error) {
      console.error('Error adding to cart:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 998,
          bgcolor: 'rgba(0,0,0,0.5)',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: 'opacity 0.3s ease',
        }}
        onClick={onClose}
      />

      <AnimatePresence mode="wait">
        {isOpen && (
          <Box
            component={motion.div}
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30,
              duration: 0.3,
            }}
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              width: '100%',
              py: 2,
              px: 2,
              background: 'white',
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              overflow: 'hidden',
              zIndex: 999,
              maxHeight: '70vh',
              overflowY: 'auto',
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                zIndex: 1,
              }}
            >
              <CloseIcon
                fontSize="medium"
                sx={{
                  color: 'text.secondary',
                }}
              />
            </IconButton>
            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                <Box
                  sx={{
                    width: '100px',
                    height: '100px',
                    borderRadius: 2,
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                >
                  <Image
                    src={mainImage}
                    alt={product?.name || 'Product'}
                    width={100}
                    height={100}
                    loader={customImageLoader}
                    style={{
                      objectFit: 'cover',
                    }}
                  />
                </Box>
                <Stack flex={1} gap={0.5}>
                  <Typography
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 500,
                      lineHeight: 1.4,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {product?.name}
                  </Typography>
                </Stack>
              </Stack>

              <ListOptions />
              <SelectQuantity />
              <Stack spacing={1} direction={'row'} width={'100%'} flexDirection={'column'}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleAddToCartAndClose}
                  loading={isLoading}
                  disabled={isLoading}
                  sx={{
                    fontWeight: 600,
                    borderRadius: '999px',
                    fontSize: '0.875rem',
                    p: '12px',
                  }}
                >
                  Thêm vào giỏ hàng
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </AnimatePresence>
    </>
  )
}

// Export component đã được wrap với HOC
export const ProductItem = withProductDetailProvider(ProductItemCore)

export const getListImagesByColor = ({
  product,
  productSelected,
}: {
  product: Product
  productSelected: CommonTypeSelectedProduct
}) => {
  const tempList = getImagesByColor(product as Product)
  const selectColor = productSelected?.attributes?.find((attr) => attr?.code === 'color')?.label
  const matchedColorGroup = tempList?.find((group) => group?.color === selectColor)
  const tempImage = productSelected?.product?.image ? [productSelected?.product?.image] : []
  return matchedColorGroup?.images && matchedColorGroup?.images?.length > 0
    ? matchedColorGroup?.images
    : tempImage
}

//#region Styled Components
const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  paddingTop: '100%',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
}))

//#region Main Image
const ChangeImageOnHover = ({
  imageDefault,
  imageHover,
  product,
  handleCardClick,
}: {
  imageDefault: string
  imageHover: string | null
  product?: Product
  handleCardClick: () => void
}) => {
  const theme = useTheme()
  const imageHoverValid = isStartWithHttp(imageHover)
  const tempIMG = useMemo(() => {
    if (!imageHover) return null
    return imageHoverValid ? imageHover : PREFIX_URL + imageHover
  }, [imageHover, imageHoverValid])

  return (
    <Box onClick={handleCardClick}>
      <ImageContainer>
        {/* Default Image */}
        <Box
          className="image-default"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            transition: 'opacity 0.5s ease-in-out',
            pointerEvents: 'none',
            borderRadius: theme.shape.borderRadius,
            overflow: 'hidden',
            opacity: 1,
            '@media (hover: hover)': {
              transition: 'opacity 0.3s ease-in-out',
            },
          }}
        >
          <Image
            src={imageDefault}
            alt={product?.name || 'Image product'}
            fill
            loader={customImageLoader}
            sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 300px"
            style={{
              objectFit: 'cover',
            }}
          />
        </Box>

        {/* Hover Image */}
        {tempIMG && (
          <Box
            className="image-hover"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              transition: 'opacity 0.5s ease-in-out',
              pointerEvents: 'none',
              borderRadius: theme.shape.borderRadius,
              overflow: 'hidden',
              opacity: 0,
              '@media (hover: hover)': {
                transition: 'opacity 0.3s ease-in-out',
              },
            }}
          >
            <Image
              src={tempIMG}
              fill
              loader={customImageLoader}
              alt={product?.name || 'Image product hover'}
              sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 300px"
              style={{
                objectFit: 'cover',
              }}
            />
          </Box>
        )}
      </ImageContainer>
    </Box>
  )
}

